import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TokenStorageService } from './token-storage.service';
import { Client } from '@stomp/stompjs';

export interface LiveChatSession {
  id: number;
  citizenUserId: number;
  citizenName?: string;
  citizenMobile?: string;
  status: 'WAITING' | 'ACTIVE' | 'CLOSED';
  assignedAgentUserId?: number;
  assignedAgentName?: string;
  assignedAgentExtension?: string;
  sourceQuestion?: string;
  sourceSimilarityScore?: number;
  createdAt?: string;
  assignedAt?: string;
  closedAt?: string;
  lastMessageAt?: string;
}

export interface LiveChatMessage {
  id: number;
  sessionId: number;
  senderType: 'CITIZEN' | 'AGENT' | 'SYSTEM';
  senderUserId?: number;
  senderName?: string;
  messageText: string;
  createdAt?: string;
  readAt?: string;
}

export interface LiveChatEvent {
  type: string;
  session?: LiveChatSession;
  message?: LiveChatMessage;
}

export interface LiveChatTransferUser {
  userId: number;
  name?: string;
  userName?: string;
  email?: string;
  mobile?: string;
  role?: string;
}

@Injectable({ providedIn: 'root' })
export class LiveChatService {
  readonly events$ = new Subject<LiveChatEvent>();
  private client: any = null;
  private subscription: any = null;

  constructor(private http: HttpClient, private tokenStorage: TokenStorageService) {}

  assignedSessions() {
    return this.http.get<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/assigned`,
      { headers: this.backgroundHeaders() }
    );
  }

  assignedHistory() {
    return this.http.get<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/assigned/history`,
      { headers: this.backgroundHeaders() }
    );
  }

  supervisorActiveSessions() {
    return this.http.get<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/active`,
      { headers: this.backgroundHeaders() }
    );
  }

  supervisorHistory() {
    return this.http.get<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/history`,
      { headers: this.backgroundHeaders() }
    );
  }

  messages(sessionId: number) {
    return this.http.get<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/${sessionId}/messages`,
      { headers: this.backgroundHeaders() }
    );
  }

  sendMessage(sessionId: number, text: string) {
    return this.http.post<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/${sessionId}/messages`,
      { text },
      { headers: this.backgroundHeaders() }
    );
  }

  close(sessionId: number) {
    return this.http.patch<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/${sessionId}/close`,
      {},
      { headers: this.backgroundHeaders() }
    );
  }

  transfer(sessionId: number, targetAgentUserId: number) {
    return this.http.patch<any>(
      `${environment.apiHost}/api/v1/live-chat/sessions/${sessionId}/transfer`,
      { targetAgentUserId },
      { headers: this.backgroundHeaders() }
    );
  }

  internalUsers() {
    return this.http.get<any>(
      `${environment.apiHost}/api/v1/user/internal/all/list`,
      { headers: this.backgroundHeaders() }
    );
  }

  connect() {
    const userId = this.tokenStorage.getUserId();
    if (!userId || this.client) return;

    this.client = new Client({
      brokerURL: this.toStompBrokerUrl(environment.stompUrl),
      reconnectDelay: 4000,
      debug: () => {}
    });
    this.client.onConnect = () => {
      this.subscription = this.client.subscribe(`/topic/live-chat/agent/${userId}`, (msg: any) => {
        const event = JSON.parse(msg.body || '{}');
        this.events$.next(event);
      });
    };
    this.client.activate();
  }

  disconnect() {
    try { this.subscription?.unsubscribe?.(); } catch {}
    try { this.client?.deactivate?.(); } catch {}
    this.subscription = null;
    this.client = null;
  }

  unwrapList<T>(res: any): T[] {
    const data = res?.data ?? res;
    return Array.isArray(data) ? data : [];
  }

  unwrap<T>(res: any): T {
    return (res?.data ?? res) as T;
  }

  private toStompBrokerUrl(url: string): string {
    const normalized = url.replace(/\/+$/, '');
    const brokerUrl = normalized.endsWith('/websocket') ? normalized : `${normalized}/websocket`;
    return brokerUrl.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
  }

  private backgroundHeaders(): HttpHeaders {
    return new HttpHeaders({ 'X-Skip-Spinner': 'true' });
  }
}
